<template>
  <div class="view processing">
    <p class="processing__eyebrow orbitron">ANALYZING YOUR FREQUENCY</p>
    <h1 class="processing__title orbitron">
      CRAFTING YOUR<br>ALTER EGO
    </h1>
    <p class="processing__sub">
      Combining your answers, vibes and energy<br>
      to reveal who you become after dark.
    </p>

    <div class="processing__orb-section">
      <!-- Left scanning items -->
      <div class="scan-col scan-col--left">
        <div class="scan-item" v-for="item in leftItems" :key="item.id" :class="{ scanned: item.scanned }">
          <div class="scan-item__icon">{{ item.icon }}</div>
          <div class="scan-item__info">
            <span class="scan-item__name orbitron">{{ item.name }}</span>
            <span class="scan-item__status">{{ item.scanned ? 'Analizat ✓' : 'Scanning...' }}</span>
          </div>
        </div>
      </div>

      <!-- Center energy orb -->
      <div class="processing__orb">
        <div class="orb-ring orb-ring--4"></div>
        <div class="orb-ring orb-ring--3"></div>
        <div class="orb-ring orb-ring--2"></div>
        <div class="orb-ring orb-ring--1"></div>
        <div class="orb-core">
          <div class="orb-inner"></div>
        </div>
        <div class="orb-platform"></div>
      </div>

      <!-- Right scanning items -->
      <div class="scan-col scan-col--right">
        <div class="scan-item" v-for="item in rightItems" :key="item.id" :class="{ scanned: item.scanned }">
          <div class="scan-item__icon">{{ item.icon }}</div>
          <div class="scan-item__info">
            <span class="scan-item__name orbitron">{{ item.name }}</span>
            <span class="scan-item__status">{{ item.scanned ? 'Analizat ✓' : 'Scanning...' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="processing__footer">
      <p class="processing__progress-label orbitron">TUNING INTO YOUR FREQUENCY...</p>
      <div class="processing__progress-track">
        <div class="processing__progress-fill" :style="{ width: progress + '%' }"></div>
        <div class="processing__progress-glow" :style="{ left: progress + '%' }"></div>
      </div>
      <span class="processing__pct orbitron">{{ Math.round(progress) }}%</span>
      <p class="processing__tip">✦ There are no wrong answers, just real vibes.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const progress = ref(0)

const leftItems = ref([
  { id: 'energy', name: 'ENERGY', icon: '〜', scanned: false },
  { id: 'vibes', name: 'VIBES', icon: '✦', scanned: false },
  { id: 'instinct', name: 'INSTINCT', icon: '◎', scanned: false },
])

const rightItems = ref([
  { id: 'freedom', name: 'FREEDOM', icon: '⚡', scanned: false },
  { id: 'connection', name: 'CONNECTION', icon: '⊙', scanned: false },
  { id: 'aura', name: 'AURA', icon: '♡', scanned: false },
])

const allItems = [...leftItems.value, ...rightItems.value]

onMounted(() => {
  const totalMs = 5000
  const start = Date.now()

  const progressTimer = setInterval(() => {
    const elapsed = Date.now() - start
    progress.value = Math.min((elapsed / totalMs) * 100, 100)
    if (progress.value >= 100) {
      clearInterval(progressTimer)
      setTimeout(() => router.push('/persona'), 300)
    }
  }, 50)

  allItems.forEach((item, i) => {
    setTimeout(() => { item.scanned = true }, (i + 1) * 700)
  })
})
</script>

<style scoped>
.processing {
  padding: 2rem 3rem;
  gap: 1rem;
  text-align: center;
}

.processing__eyebrow {
  font-size: 0.8rem;
  letter-spacing: 0.5em;
  color: rgba(139, 92, 246, 0.6);
}

.processing__title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0.08em;
  color: #fff;
  text-shadow: 0 0 40px rgba(139, 92, 246, 0.7), 0 0 80px rgba(59, 130, 246, 0.4);
}

.processing__sub {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.05em;
  line-height: 1.6;
}

.processing__orb-section {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 3rem;
  width: 100%;
  max-width: 1100px;
}

/* ─── Scan columns ─── */
.scan-col {
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
}

.scan-col--left { align-items: flex-end; }
.scan-col--right { align-items: flex-start; }

.scan-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0.4;
  transition: opacity 0.5s ease;
}

.scan-item.scanned { opacity: 1; }

.scan-col--left .scan-item { flex-direction: row-reverse; }

.scan-item__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(139, 92, 246, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: rgba(139, 92, 246, 0.7);
  flex-shrink: 0;
  transition: all 0.5s ease;
}

.scan-item.scanned .scan-item__icon {
  border-color: rgba(139, 92, 246, 0.9);
  color: #fff;
  box-shadow: 0 0 15px rgba(139, 92, 246, 0.5);
  background: rgba(139, 92, 246, 0.1);
}

.scan-item__info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.scan-col--left .scan-item__info { text-align: right; }

.scan-item__name {
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #fff;
}

.scan-item__status {
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  color: rgba(139, 92, 246, 0.6);
}

/* ─── Energy orb ─── */
.processing__orb {
  position: relative;
  width: 300px;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.orb-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid;
  top: 50%;
  left: 50%;
  transform-origin: center;
}

.orb-ring--1 {
  width: 170px; height: 170px;
  margin-top: -85px; margin-left: -85px;
  border-color: rgba(139, 92, 246, 0.7);
  box-shadow: 0 0 15px rgba(139, 92, 246, 0.3);
  animation: orbRotate 6s linear infinite;
}

.orb-ring--2 {
  width: 220px; height: 180px;
  margin-top: -90px; margin-left: -110px;
  border-color: rgba(59, 130, 246, 0.5);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.2);
  animation: orbRotate 9s linear infinite reverse;
}

.orb-ring--3 {
  width: 250px; height: 210px;
  margin-top: -105px; margin-left: -125px;
  border-color: rgba(168, 85, 247, 0.35);
  animation: orbRotate 14s linear infinite;
}

.orb-ring--4 {
  width: 280px; height: 240px;
  margin-top: -120px; margin-left: -140px;
  border-color: rgba(96, 165, 250, 0.2);
  animation: orbRotate 20s linear infinite reverse;
}

.orb-core {
  position: relative;
  z-index: 2;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, rgba(168, 85, 247, 0.9) 0%, rgba(109, 40, 217, 0.7) 40%, rgba(59, 130, 246, 0.5) 70%, transparent 100%);
  box-shadow:
    0 0 40px rgba(139, 92, 246, 0.7),
    0 0 80px rgba(139, 92, 246, 0.4),
    0 0 120px rgba(59, 130, 246, 0.2);
  animation: orbPulse 3s ease-in-out infinite;
}

.orb-inner {
  position: absolute;
  top: 20%; left: 20%;
  width: 30%;
  height: 30%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  filter: blur(6px);
}

.orb-platform {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 40px;
  background: radial-gradient(ellipse at center, rgba(139, 92, 246, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(8px);
}

@keyframes orbRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes orbPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 40px rgba(139, 92, 246, 0.7), 0 0 80px rgba(139, 92, 246, 0.4), 0 0 120px rgba(59, 130, 246, 0.2); }
  50% { transform: scale(1.08); box-shadow: 0 0 60px rgba(139, 92, 246, 0.9), 0 0 100px rgba(139, 92, 246, 0.5), 0 0 150px rgba(59, 130, 246, 0.3); }
}

/* ─── Footer ─── */
.processing__footer {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.processing__progress-label {
  font-size: 0.75rem;
  letter-spacing: 0.35em;
  color: rgba(255, 255, 255, 0.4);
}

.processing__progress-track {
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: visible;
}

.processing__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7c3aed, #60a5fa);
  border-radius: 4px;
  transition: width 0.1s linear;
  box-shadow: 0 0 10px rgba(139, 92, 246, 0.7);
}

.processing__progress-glow {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 10px rgba(139, 92, 246, 1), 0 0 20px rgba(96, 165, 250, 0.8);
  transition: left 0.1s linear;
}

.processing__pct {
  font-size: 1rem;
  letter-spacing: 0.1em;
  color: rgba(139, 92, 246, 0.9);
  align-self: flex-end;
}

.processing__tip {
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.2);
  margin-top: 0.3rem;
}
</style>
